import { useState } from 'react';
import { Plus, Upload } from 'lucide-react';

export default function CreateNewTemplate() {
    const [templateName, setTemplateName] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [lineItems, setLineItems] = useState([
        { id: 1, name: '', description: '', quantity: 1, unitCost: 0, unitPrice: 0 }
    ]);
    const [clientMessage, setClientMessage] = useState('');
    const [disclaimer, setDisclaimer] = useState('This quote is valid for the next 30 days, after which values may be subject to change.');
    const [activeSection, setActiveSection] = useState('introduction');

    const addLineItem = (type = 'regular') => {
        const newItem = {
            id: Date.now(),
            name: '',
            description: '',
            quantity: 1,
            unitCost: 0,
            unitPrice: 0,
            type
        };
        setLineItems([...lineItems, newItem]);
    };

    const updateLineItem = (id, field, value) => {
        setLineItems(lineItems.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const removeLineItem = (id) => {
        setLineItems(lineItems.filter(item => item.id !== id));
    };

    const calculateTotal = (item) => {
        return (item.quantity * item.unitPrice).toFixed(2);
    };

    const calculateSubtotal = () => {
        return lineItems.reduce((sum, item) =>
            sum + (item.quantity * item.unitPrice), 0
        ).toFixed(2);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                {/* Decorative Top Border */}
                <div className="h-1.5 bg-gradient-to-r from-green-400 via-green-500 to-green-600"></div>

                {/* Header */}
                <div className="border-b border-gray-200 p-6 bg-gradient-to-b from-green-50/30 to-white">
                    <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <span className="w-1 h-8 bg-gradient-to-b from-green-500 to-green-600 rounded-full"></span>
                        New Template
                    </h1>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Template name
                            </label>
                            <input
                                type="text"
                                placeholder="Template name"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-gray-400"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Job title
                            </label>
                            <input
                                type="text"
                                placeholder="Title"
                                value={jobTitle}
                                onChange={(e) => setJobTitle(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <button className="mt-4 text-green-600 hover:text-green-700 text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all duration-200">
                        <Plus size={16} />
                        Add Section
                    </button>

                    <div className="mt-4 flex gap-2">
                        <button
                            onClick={() => setActiveSection('introduction')}
                            className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 ${activeSection === 'introduction'
                                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-100 border border-transparent hover:border-gray-300'
                                }`}
                        >
                            Introduction
                        </button>
                    </div>
                </div>

                {/* Product/Service Section */}
                <div className="p-6 border-b border-gray-200 bg-gradient-to-b from-white to-gray-50/30">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-1 h-6 bg-gradient-to-b from-green-500 to-green-600 rounded-full"></span>
                        Product / Service
                    </h2>

                    <div className="space-y-4">
                        {lineItems.map((item, index) => (
                            <div key={item.id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-green-300 transition-all duration-200 bg-white shadow-sm hover:shadow-md">
                                <div className="flex items-start gap-3 mb-3">
                                    <input
                                        type="text"
                                        placeholder="Name"
                                        value={item.name}
                                        onChange={(e) => updateLineItem(item.id, 'name', e.target.value)}
                                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-gray-400"
                                    />
                                    <div className="flex gap-2">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                                            <input
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                                className="w-20 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Unit cost</label>
                                            <div className="flex items-center">
                                                <span className="text-gray-500 mr-1">$</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={item.unitCost}
                                                    onChange={(e) => updateLineItem(item.id, 'unitCost', parseFloat(e.target.value) || 0)}
                                                    className="w-24 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Unit price</label>
                                            <div className="flex items-center">
                                                <span className="text-gray-500 mr-1">$</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={item.unitPrice}
                                                    onChange={(e) => updateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                    className="w-24 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Total</label>
                                            <div className="flex items-center">
                                                <span className="text-gray-500 mr-1">$</span>
                                                <input
                                                    type="text"
                                                    value={calculateTotal(item)}
                                                    readOnly
                                                    className="w-24 px-2 py-2 border border-gray-300 rounded-md bg-gray-50"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <textarea
                                        placeholder="Description"
                                        value={item.description}
                                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                                        rows="2"
                                    />

                                    <div className="mt-2 flex items-center justify-between">
                                        <button className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                            <Upload size={14} />
                                            Upload image
                                        </button>
                                        {lineItems.length > 1 && (
                                            <button
                                                onClick={() => removeLineItem(item.id)}
                                                className="text-sm text-red-600 hover:text-red-700"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Select or drag an image here to upload</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-2 mt-4">
                        <button
                            onClick={() => addLineItem('regular')}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                        >
                            New line item
                        </button>
                        <button
                            onClick={() => addLineItem('optional')}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                        >
                            New optional line item
                        </button>
                        <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                            New text
                        </button>
                    </div>

                    {/* Totals */}
                    <div className="mt-6 flex justify-end">
                        <div className="w-80 space-y-2">
                            <div className="flex justify-between items-center py-2">
                                <span className="text-gray-700">Subtotal</span>
                                <span className="font-semibold">${calculateSubtotal()}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-t border-gray-200">
                                <span className="font-semibold text-gray-900">Total</span>
                                <span className="font-semibold text-gray-900">${calculateSubtotal()}</span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="text-gray-700">Required deposit</span>
                                <button className="text-blue-600 hover:text-blue-700 text-sm">
                                    Add Required Deposit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Sections */}
                <div className="p-6 border-b border-gray-200">
                    <button className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center gap-1">
                        <Plus size={16} />
                        Add Section
                    </button>

                    <div className="mt-4 flex gap-2">
                        <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">
                            Attachments
                        </button>
                        <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">
                            Images
                        </button>
                        <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">
                            Reviews
                        </button>
                    </div>
                </div>

                {/* Client Message */}
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">Client message</h2>
                    <textarea
                        placeholder="Details"
                        value={clientMessage}
                        onChange={(e) => setClientMessage(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                        rows="4"
                    />
                </div>

                {/* Contract/Disclaimer */}
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">Contract / Disclaimer</h2>
                    <textarea
                        value={disclaimer}
                        onChange={(e) => setDisclaimer(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                        rows="2"
                    />
                </div>

                {/* Footer Actions */}
                <div className="p-6 flex justify-between items-center">
                    <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                        Cancel
                    </button>
                    <button className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium">
                        Save Template
                    </button>
                </div>
            </div>
        </div>
    );
}